import { useMemo, useState } from 'react';
import { Row, Col, Card, Form, Alert, Spinner, Badge } from 'react-bootstrap';
import ReactEChartsCore from '../../components/charts/EChart';
import * as echarts from 'echarts/core';
import { TooltipComponent, GridComponent, LegendComponent } from 'echarts/components';
import { BarChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import { useAppContext } from '../../providers/AppProvider';
import AdvanceTable from '../../components/base/AdvanceTable';
import AdvanceTableFooter from '../../components/base/AdvanceTableFooter';
import AdvanceTableProvider from '../../providers/AdvanceTableProvider';
import useAdvanceTable from '../../hooks/useAdvanceTable';
import SearchBox from '../../components/common/SearchBox';
import {
  useOperatorSummary,
  useOperatorMaterials,
  useOperatorTokens,
  useOperatorDowntime
} from '../../features/operator-dashboard/hooks/useOperatorDashboard';

echarts.use([TooltipComponent, GridComponent, LegendComponent, BarChart, CanvasRenderer]);

// Categorical palette, assigned to material identity in a FIXED order and never
// cycled — a filter that drops a material must not repaint the survivors. Both
// sets were checked with the palette validator (lightness band, chroma floor,
// CVD separation, contrast); the dark steps are darker, not a lightened flip.
const MATERIAL_COLORS = {
  light: { copper: '#e5780b', pvc: '#3874ff', pe: '#10b981', blr: '#8b5cf6', other: '#f43f5e' },
  dark: { copper: '#c2660a', pvc: '#2f6ae0', pe: '#0d9b6c', blr: '#7c4fe0', other: '#e0344f' }
};
const MATERIAL_ORDER = ['copper', 'pvc', 'pe', 'blr', 'other'];
const MATERIAL_LABEL = { copper: 'Copper', pvc: 'PVC', pe: 'PE', blr: 'BLR', other: 'Other' };

const fmt = (v, d = 0) =>
  Number(v || 0).toLocaleString('ro-RO', { minimumFractionDigits: d, maximumFractionDigits: d });

const fmtDur = sec => {
  const s = Number(sec || 0);
  if (!s) return '—';
  const h = Math.floor(s / 3600);
  const m = Math.round((s % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const fmtDate = ts => {
  if (!ts) return '—';
  const d = new Date(Number(ts));
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('ro-RO', { dateStyle: 'short', timeStyle: 'short' });
};

const KpiCard = ({ title, value, sub, tone }) => (
  <Card className={`h-100 shadow-sm ${tone ? `border-start border-4 border-${tone}` : ''}`}>
    <Card.Body className="py-3">
      <div className="text-700 fs-9 fw-semibold text-uppercase mb-1 text-nowrap">{title}</div>
      <div className="fs-4 fw-bold text-1100 text-nowrap">{value}</div>
      {sub && <div className="text-700 fs-10 mt-1">{sub}</div>}
    </Card.Body>
  </Card>
);

const ChartCard = ({ title, subtitle, children, height = 340 }) => (
  <Card className="h-100">
    <Card.Body>
      <h5 className="mb-0">{title}</h5>
      {subtitle && <p className="text-700 fs-10 mb-2">{subtitle}</p>}
      <div style={{ height }}>{children}</div>
    </Card.Body>
  </Card>
);

// Shared axis/grid skin: recessive grid and axes, text in ink tokens rather than
// series colours, horizontal bars so operator names stay readable at any count.
const baseOptions = (getThemeColor, categories, unit) => ({
  grid: { left: 8, right: 24, top: 8, bottom: 8, containLabel: true },
  xAxis: {
    type: 'value',
    axisLabel: { color: getThemeColor('gray-700'), fontSize: 11, formatter: `{value} ${unit}` },
    splitLine: { lineStyle: { color: getThemeColor('gray-200') } }
  },
  yAxis: {
    type: 'category',
    data: categories,
    axisLabel: { color: getThemeColor('gray-800'), fontSize: 11 },
    axisLine: { lineStyle: { color: getThemeColor('gray-300') } },
    axisTick: { show: false }
  }
});

const OperatorManagement = () => {
  const {
    config: { theme },
    getThemeColor
  } = useAppContext();

  const [filters, setFilters] = useState({ phase: '', operator: '', machineId: '', status: '' });
  const [search, setSearch] = useState('');

  const summaryQ = useOperatorSummary();
  const materialsQ = useOperatorMaterials();
  const tokensQ = useOperatorTokens(filters);
  const downtimeQ = useOperatorDowntime(30);

  const palette = MATERIAL_COLORS[theme === 'dark' ? 'dark' : 'light'];
  const totals = summaryQ.data?.totals || {};
  const operators = useMemo(() => summaryQ.data?.operators || [], [summaryQ.data]);
  const scope = summaryQ.data?.scope || {};

  // Named people only — the unattributed bucket is a gap in the data, not a
  // worker, and putting it in a ranking chart would make it look like one.
  const named = useMemo(
    () => operators.filter(o => o.operator !== '(unassigned)'),
    [operators]
  );

  const copperOption = useMemo(() => {
    const rows = named.filter(o => o.byCategory?.copper > 0).slice().reverse();
    return {
      ...baseOptions(getThemeColor, rows.map(o => o.operator), 'kg'),
      tooltip: {
        trigger: 'item',
        formatter: p => `<strong>${p.name}</strong><br/>Copper: ${fmt(p.value, 2)} kg`
      },
      series: [
        {
          type: 'bar',
          data: rows.map(o => o.byCategory.copper),
          itemStyle: { color: palette.copper, borderRadius: [0, 4, 4, 0] },
          barMaxWidth: 18,
          // The palette's contrast warning against a light surface is answered
          // by labelling every bar, and by the table below.
          label: {
            show: true,
            position: 'right',
            color: getThemeColor('gray-800'),
            fontSize: 11,
            formatter: p => `${fmt(p.value, 1)} kg`
          }
        }
      ]
    };
  }, [named, palette, getThemeColor]);

  const materialOption = useMemo(() => {
    const rows = named.slice().reverse();
    const present = MATERIAL_ORDER.filter(c => rows.some(o => (o.byCategory?.[c] || 0) > 0));
    return {
      ...baseOptions(getThemeColor, rows.map(o => o.operator), 'kg'),
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        valueFormatter: v => `${fmt(v, 2)} kg`
      },
      legend: {
        data: present.map(c => MATERIAL_LABEL[c]),
        bottom: 0,
        textStyle: { color: getThemeColor('gray-800'), fontSize: 11 }
      },
      grid: { left: 8, right: 24, top: 8, bottom: 28, containLabel: true },
      series: present.map(c => ({
        name: MATERIAL_LABEL[c],
        type: 'bar',
        stack: 'material',
        data: rows.map(o => o.byCategory?.[c] || 0),
        // 2px of surface between stacked segments keeps the boundaries legible
        itemStyle: { color: palette[c], borderColor: getThemeColor('body-bg'), borderWidth: 2 },
        barMaxWidth: 18
      }))
    };
  }, [named, palette, getThemeColor]);

  const phaseOption = useMemo(() => {
    const byPhase = summaryQ.data?.byPhase || [];
    const rows = named.slice().reverse();
    const phases = [...new Set(byPhase.map(r => r.phase).filter(Boolean))].sort();
    const lookup = new Map(byPhase.map(r => [`${r.operator}|${r.phase}`, r.meters]));
    // Phases are an ordered sequence, so they read as one hue stepping darker
    // rather than as unrelated categories.
    const shade = i => {
      const steps = Math.max(1, phases.length - 1);
      const l = 68 - (i / steps) * 30;
      return `hsl(217, 72%, ${l}%)`;
    };
    return {
      ...baseOptions(getThemeColor, rows.map(o => o.operator), 'm'),
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' }, valueFormatter: v => `${fmt(v)} m` },
      legend: { data: phases, bottom: 0, textStyle: { color: getThemeColor('gray-800'), fontSize: 11 } },
      grid: { left: 8, right: 24, top: 8, bottom: 28, containLabel: true },
      series: phases.map((p, i) => ({
        name: p,
        type: 'bar',
        stack: 'phase',
        data: rows.map(o => lookup.get(`${o.operator}|${p}`) || 0),
        itemStyle: { color: shade(i), borderColor: getThemeColor('body-bg'), borderWidth: 2 },
        barMaxWidth: 18
      }))
    };
  }, [summaryQ.data, named, getThemeColor]);

  const speedOption = useMemo(() => {
    const rows = named.filter(o => o.metersPerHour > 0).slice().reverse();
    return {
      ...baseOptions(getThemeColor, rows.map(o => o.operator), 'm/h'),
      tooltip: {
        trigger: 'item',
        formatter: p => {
          const o = rows[p.dataIndex];
          return `<strong>${p.name}</strong><br/>${fmt(p.value)} m/h<br/>${fmt(o.meters)} m in ${fmtDur(o.runningSec)} running`;
        }
      },
      series: [
        {
          type: 'bar',
          data: rows.map(o => o.metersPerHour),
          itemStyle: { color: palette.pvc, borderRadius: [0, 4, 4, 0] },
          barMaxWidth: 18,
          label: {
            show: true,
            position: 'right',
            color: getThemeColor('gray-800'),
            fontSize: 11,
            formatter: p => fmt(p.value)
          }
        }
      ]
    };
  }, [named, palette, getThemeColor]);

  const downtimeOption = useMemo(() => {
    const rows = (downtimeQ.data?.rows || [])
      .filter(r => r.operator !== '(unassigned)' && r.costSec > 0)
      .slice()
      .reverse();
    return {
      ...baseOptions(getThemeColor, rows.map(r => r.operator), ''),
      xAxis: {
        type: 'value',
        axisLabel: {
          color: getThemeColor('gray-700'),
          fontSize: 11,
          formatter: v => `${Math.round(v / 60)}m`
        },
        splitLine: { lineStyle: { color: getThemeColor('gray-200') } }
      },
      tooltip: {
        trigger: 'item',
        formatter: p => {
          const r = rows[p.dataIndex];
          return `<strong>${p.name}</strong><br/>Worked time on stops: ${fmtDur(r.costSec)}<br/>${r.events} stop(s), avg ${fmtDur(r.avgCostSec)}`;
        }
      },
      series: [
        {
          type: 'bar',
          data: rows.map(r => r.costSec),
          itemStyle: { color: palette.other, borderRadius: [0, 4, 4, 0] },
          barMaxWidth: 18,
          label: {
            show: true,
            position: 'right',
            color: getThemeColor('gray-800'),
            fontSize: 11,
            formatter: p => fmtDur(p.value)
          }
        }
      ]
    };
  }, [downtimeQ.data, palette, getThemeColor]);

  // ── token table ──
  const tokenRows = useMemo(() => {
    const rows = tokensQ.data?.rows || [];
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r =>
      [r.productLabel, r.tokenId, r.jobCode, r.phase, r.machineId, r.operatorLabel]
        .filter(Boolean)
        .some(v => String(v).toLowerCase().includes(q))
    );
  }, [tokensQ.data, search]);

  const columns = useMemo(
    () => [
      {
        accessorKey: 'productLabel',
        header: 'Product',
        cell: ({ row: { original: r } }) => (
          <div>
            <div className="fw-semibold text-nowrap">{r.productLabel || '—'}</div>
            <div className="text-700 fs-10">{r.jobCode || r.jobId}</div>
          </div>
        )
      },
      {
        accessorKey: 'tokenId',
        header: 'Drum',
        cell: ({ row: { original: r } }) => (
          <div className="text-nowrap">
            <span className="fw-semibold">{r.tokenId}</span>
            {r.live && (
              <Badge bg="success" className="ms-2">
                live
              </Badge>
            )}
          </div>
        )
      },
      { accessorKey: 'phase', header: 'Phase', cell: ({ row }) => row.original.phase || '—' },
      {
        accessorKey: 'machineId',
        header: 'Machine',
        cell: ({ row }) => (row.original.machineId || '—').toUpperCase()
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row: { original: r } }) => {
          const done = ['DONE', 'COMPLETED', 'FINISHED'].includes(r.status);
          return (
            <Badge bg={done ? 'success' : r.status === 'ACTIVE' ? 'primary' : 'secondary'}>
              {r.status}
            </Badge>
          );
        }
      },
      {
        accessorKey: 'operatorLabel',
        header: 'Operator(s)',
        cell: ({ row: { original: r } }) => {
          if (!r.operators?.length) return <span className="text-700">—</span>;
          // A drum run across a handover lists everyone, with the metres each of
          // them actually produced — the number the whole feature rests on.
          return (
            <div>
              {r.operators.map(o => (
                <div key={o.operator} className="text-nowrap">
                  <span className={o.operator === '(unassigned)' ? 'text-warning' : ''}>
                    {o.operator}
                  </span>
                  <span className="text-700 fs-10 ms-2">{fmt(o.meters)} m</span>
                </div>
              ))}
            </div>
          );
        }
      },
      {
        accessorKey: 'producedLength',
        header: 'Produced',
        cell: ({ row: { original: r } }) => (
          <div className="text-nowrap">
            {fmt(r.producedLength)} / {fmt(r.plannedLength)} m
          </div>
        )
      },
      {
        id: 'materials',
        header: 'Material share',
        cell: ({ row: { original: r } }) =>
          r.materials?.length ? (
            <div>
              {r.materials.map(m => (
                <div key={m.label} className="text-nowrap fs-10">
                  <span
                    className="d-inline-block rounded-circle me-1"
                    style={{ width: 8, height: 8, background: palette[m.category] || palette.other }}
                  />
                  {m.label}: {fmt(m.kg, 2)} kg
                </div>
              ))}
            </div>
          ) : (
            <span className="text-700">—</span>
          )
      },
      {
        accessorKey: 'completedAt',
        header: 'Finished',
        cell: ({ row }) => <span className="text-nowrap">{fmtDate(row.original.completedAt)}</span>
      }
    ],
    [palette]
  );

  const table = useAdvanceTable({
    data: tokenRows,
    columns,
    pageSize: 15,
    pagination: true,
    sortable: true
  });

  const options = tokensQ.data?.options || {};
  const isLoading = summaryQ.isLoading;
  const error = summaryQ.error || materialsQ.error || tokensQ.error;

  return (
    <>
      <Row className="gy-3 mb-4 justify-content-between align-items-center">
        <Col xl={5} lg={12}>
          <h2 className="mb-2 text-1100">Operator Management</h2>
          <h5 className="text-700 fw-semi-bold mb-0">
            Everything still running in the factory, seen per person
          </h5>
        </Col>
        <Col xl={7} lg={12}>
          <Row className="g-2 justify-content-end">
            <Col xs={6} md={3}>
              <KpiCard title="Produced" value={`${fmt(totals.meters)} m`} tone="primary" />
            </Col>
            <Col xs={6} md={3}>
              <KpiCard title="Copper" value={`${fmt(totals.copper_kg, 1)} kg`} tone="warning" />
            </Col>
            <Col xs={6} md={3}>
              <KpiCard
                title="Operators"
                value={fmt(totals.operatorCount)}
                sub={`${scope.jobs || 0} jobs · ${scope.tasks || 0} tasks`}
                tone="info"
              />
            </Col>
            <Col xs={6} md={3}>
              {/* Two rates, because they disagree: a task that ran before
                  operator tracking existed has no metres but all its material. */}
              <KpiCard
                title="Attributed"
                value={`${fmt(totals.materialAttributionRate, 1)}%`}
                sub={`of material · ${fmt(totals.attributionRate, 1)}% of metres`}
                tone={totals.materialAttributionRate >= 80 ? 'success' : 'secondary'}
              />
            </Col>
          </Row>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger">Failed to load operator data: {error.message || 'Unknown error'}</Alert>
      )}

      {totals.unattributedKg > 0 && (
        <Alert variant="warning" className="py-2 fs-9">
          <strong>{fmt(totals.unattributedKg, 1)} kg</strong> of material and{' '}
          <strong>{fmt(totals.unattributedMeters)} m</strong> have no operator on record — work
          done before operator tracking started, or drums nobody logged in for. They are shown
          under <em>(unassigned)</em> rather than dropped.
        </Alert>
      )}

      {isLoading ? (
        <div className="d-flex align-items-center justify-content-center py-6">
          <Spinner animation="border" size="sm" className="me-2" />
          <span>Loading operator data...</span>
        </div>
      ) : (
        <>
          <Row className="g-3 mb-3">
            <Col xs={12} xl={6}>
              <ChartCard
                title="Copper per operator"
                subtitle="Only the entry phases (pre-stranding, conductor isolation) consume copper — later phases re-handle the same metal"
              >
                <ReactEChartsCore
                  echarts={echarts}
                  option={copperOption}
                  style={{ height: '100%', width: '100%' }}
                  notMerge
                />
              </ChartCard>
            </Col>
            <Col xs={12} xl={6}>
              <ChartCard title="All material per operator" subtitle="Split by the metres each person produced">
                <ReactEChartsCore
                  echarts={echarts}
                  option={materialOption}
                  style={{ height: '100%', width: '100%' }}
                  notMerge
                />
              </ChartCard>
            </Col>
          </Row>

          <Row className="g-3 mb-3">
            <Col xs={12} xl={6}>
              <ChartCard title="Length produced per operator" subtitle="Stacked by phase">
                <ReactEChartsCore
                  echarts={echarts}
                  option={phaseOption}
                  style={{ height: '100%', width: '100%' }}
                  notMerge
                />
              </ChartCard>
            </Col>
            <Col xs={12} xl={6}>
              <ChartCard
                title="Effective speed"
                subtitle="Metres per hour of line movement — idle time around the work is excluded"
              >
                <ReactEChartsCore
                  echarts={echarts}
                  option={speedOption}
                  style={{ height: '100%', width: '100%' }}
                  notMerge
                />
              </ChartCard>
            </Col>
          </Row>

          <Row className="g-3 mb-4">
            <Col xs={12} xl={6}>
              <ChartCard
                title="Time spent on stoppages"
                subtitle="Last 30 days — only stops somebody actually worked on are charged"
              >
                <ReactEChartsCore
                  echarts={echarts}
                  option={downtimeOption}
                  style={{ height: '100%', width: '100%' }}
                  notMerge
                />
              </ChartCard>
            </Col>
            <Col xs={12} xl={6}>
              <Card className="h-100">
                <Card.Body>
                  <h5 className="mb-0">Changeovers</h5>
                  <p className="text-700 fs-10 mb-3">Last 30 days, per operator</p>
                  <div className="table-responsive" style={{ maxHeight: 300 }}>
                    <table className="table table-sm align-middle mb-0">
                      <thead>
                        <tr className="text-700 fs-10 text-uppercase">
                          <th>Operator</th>
                          <th className="text-end">Count</th>
                          <th className="text-end">Total</th>
                          <th className="text-end">Average</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(downtimeQ.data?.changeovers || []).map(c => (
                          <tr key={c.operator}>
                            <td className={c.operator === '(unassigned)' ? 'text-warning' : ''}>
                              {c.operator}
                            </td>
                            <td className="text-end">{c.count}</td>
                            <td className="text-end">{fmtDur(c.totalSec)}</td>
                            <td className="text-end">{fmtDur(c.avgSec)}</td>
                          </tr>
                        ))}
                        {!(downtimeQ.data?.changeovers || []).length && (
                          <tr>
                            <td colSpan={4} className="text-700 text-center py-4">
                              No changeovers recorded in this window.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-body-emphasis pt-5 pb-3 border-top border-translucent">
            <h3>Drums</h3>
            <p className="text-700 lh-sm mb-4">
              One row per drum across every phase, finished ones included — {tokensQ.data?.count ?? 0}{' '}
              of {tokensQ.data?.total ?? 0} shown
            </p>

            <AdvanceTableProvider {...table}>
              <div className="mb-3 d-flex flex-wrap gap-2 align-items-center">
                <SearchBox
                  placeholder="Search product, drum, operator..."
                  onChange={e => setSearch(e.target.value)}
                />
                <Form.Select
                  size="sm"
                  style={{ width: 'auto' }}
                  value={filters.phase}
                  onChange={e => setFilters(f => ({ ...f, phase: e.target.value }))}
                >
                  <option value="">All phases</option>
                  {(options.phases || []).map(p => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </Form.Select>
                <Form.Select
                  size="sm"
                  style={{ width: 'auto' }}
                  value={filters.operator}
                  onChange={e => setFilters(f => ({ ...f, operator: e.target.value }))}
                >
                  <option value="">All operators</option>
                  {(options.operators || []).map(o => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </Form.Select>
                <Form.Select
                  size="sm"
                  style={{ width: 'auto' }}
                  value={filters.machineId}
                  onChange={e => setFilters(f => ({ ...f, machineId: e.target.value }))}
                >
                  <option value="">All machines</option>
                  {(options.machines || []).map(m => (
                    <option key={m} value={m}>
                      {m.toUpperCase()}
                    </option>
                  ))}
                </Form.Select>
                <Form.Select
                  size="sm"
                  style={{ width: 'auto' }}
                  value={filters.status}
                  onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
                >
                  <option value="">All statuses</option>
                  {(options.statuses || []).map(s => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </Form.Select>
              </div>

              <AdvanceTable tableProps={{ className: 'phoenix-table fs-9' }} />
              <AdvanceTableFooter navBtn />
            </AdvanceTableProvider>
          </div>
        </>
      )}
    </>
  );
};

export default OperatorManagement;
